/*
 * HomeCategoryCards — one card per menu group (US, Canada, Brazil, …) under
 * the homepage hero. Groups, subs and counts all come from the categories
 * API, so a new group shows up here without a code change.
 */

import { FC } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { CategoryInfo } from '@/services/client/quiz';
import { Icon } from '@/components';

import './index.scss';

interface Group {
  name: string;
  subs: string[];
  questions: number;
  exams: number;
}

const HomeCategoryCards: FC<{ categories: CategoryInfo[] }> = ({
  categories,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'home_hero' });

  const byName = new Map<string, Group>();
  categories.forEach((c) => {
    const name = c.menu_group || c.name;
    const g = byName.get(name) || { name, subs: [], questions: 0, exams: 0 };
    const sub = c.menu_sub || c.name;
    if (!g.subs.includes(sub)) g.subs.push(sub);
    g.questions += c.question_count || 0;
    g.exams += c.exam_count || 0;
    byName.set(name, g);
  });
  const groups = Array.from(byName.values())
    .filter((g) => g.exams > 0)
    .sort((a, b) => b.questions - a.questions);

  if (!groups.length) return null;

  return (
    <div className="home-cards mb-2">
      {groups.map((g) => (
        <Link
          key={g.name}
          to={`/public-tests?group=${encodeURIComponent(g.name)}`}
          className="home-card">
          <div className="home-card__top">
            <span className="home-card__badge">{g.name}</span>
            <span className="home-card__meta">
              {t('card_questions', { count: g.questions })}
            </span>
          </div>
          <h3 className="home-card__title">{g.subs.join(' · ')}</h3>
          <p className="home-card__desc">
            {t('card_tests', { count: g.exams })}
          </p>
          <div className="home-card__bottom">
            <span className="home-card__cta">{t('card_cta')} →</span>
            <span className="home-card__icon">
              <Icon name="journal-check" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default HomeCategoryCards;
